<template>
  <div>
    <!-- Loading State: Driven by the persistent cardStore -->
    <div v-if="cardStore.isLoading" class="loading-container">
      <div class="spinner"></div>
      <p class="loading-text">{{ $t('loading') || 'Loading data, please wait...' }}</p>
    </div>

    <!-- Content State -->
    <div v-else>
      <!-- Display messages to user: Also driven by the cardStore -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      <div v-if="isEmptyPrivateSector" class="info-message">
        {{ $t('noPrivateSectorsAvailable') }}
      </div>

      <!-- Display cards only if there are any -->
      <CardComponent
        v-if="filteredCards.length > 0"
        :cards="filteredCards"
        :sector-title="sectorTitle"
        :cards-per-page="cardsPerPage"
        @card-clicked="handleCardClick"
      />
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCardStore } from '@/stores/cardStore';
import { useSectorStore } from '@/stores/sector';
import { useStructureStore } from '@/stores/structure';
import CardComponent from '@/components/common/CardComponent.vue';

export default {
  name: 'CardPage',
  components: {
    CardComponent,
  },
  data() {
    return {
      sectorType: '', // Will be 'public' or 'private'
      cardsPerPage: 10,
    };
  },
  computed: {
    ...mapStores(useCardStore, useSectorStore, useStructureStore),
    cards() {
      if (this.sectorType === 'public') return this.cardStore.publicCards;
      if (this.sectorType === 'private') return this.cardStore.privateCards;
      return [];
    },
    filteredCards() {
      return this.cards.filter(card => card && card.id);
    },
    isLoading() {
      return this.cardStore.isLoading;
    },
    errorMessage() {
      return this.cardStore.error;
    },
    sectorTitle() {
      return this.sectorType === 'public' ? this.$t('publicSector') : this.$t('privateSector');
    },
    isEmptyPrivateSector() {
      return !this.isLoading && this.sectorType === 'private' && this.cards.length === 0;
    },
  },
  methods: {
    handleCardClick(card) {
      if (!card?.id) {
        return;
      }

      this.structureStore.clearCurrentStructure();
      this.sectorStore.setCurrentSector({
        id: card.id,
        name: card.name || 'Unnamed Sector'
      });
      
      const routeName = this.sectorType === 'private' 
        ? 'private-structure-list' 
        : 'structure-list';

      this.$router.push({
        name: routeName,
        params: { 
          lang: this.$i18n.locale,
          name: encodeURIComponent(card.name || card.id) 
        }
      });
    },

    determineSectorTypeAndFetch() {
      const newSectorType = this.$route.params.sectorType?.toLowerCase();
      
      if (newSectorType && (newSectorType === 'public' || newSectorType === 'private')) {
        this.sectorType = newSectorType;
        
        // --- THIS IS THE ONLY CHANGE IN THIS FILE ---
        // We now pass the current language from vue-i18n to the store's fetch action.
        this.cardStore.fetchCards(this.sectorType, this.$i18n.locale);

      } else {
        this.sectorType = '';
        this.cardStore.clearCards();
      }
    }
  },
  watch: {
    '$route.path': {
      handler() {
        this.determineSectorTypeAndFetch();
      },
      immediate: true,
    },
    // ADDED: It's good practice to also watch for language changes directly
    '$i18n.locale': {
        handler() {
            this.determineSectorTypeAndFetch();
        }
    }
  },
};
</script>

<style scoped>
/* Your existing styles are fine. */
.loading-container { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 40px; min-height: 300px; text-align: center; }
.spinner { width: 48px; height: 48px; border: 5px solid #f3f3f3; border-top: 5px solid #3498db; border-radius: 50%; animation: spin 1.2s linear infinite; margin-bottom: 16px; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.loading-text { font-size: 1.1rem; color: #555; }
.error-message { color: #d32f2f; background-color: #ffebee; padding: 12px; border-radius: 4px; margin-bottom: 16px; text-align: center; }
.info-message { color: #1976d2; background-color: #e3f2fd; padding: 12px; border-radius: 4px; margin-bottom: 16px; text-align: center; }
</style>